import { QueryClient } from '@tanstack/react-query';

interface InvalidationRequest {
  queryKey: unknown[];
  timestamp: number;
}

class DebouncedQueryInvalidator {
  private invalidationRequests = new Map<string, InvalidationRequest>();

  private timeoutId: NodeJS.Timeout | null = null;

  private readonly debounceMs: number;

  private readonly queryClient: QueryClient;

  constructor(queryClient: QueryClient, debounceMs = 300) {
    this.queryClient = queryClient;
    this.debounceMs = debounceMs;
  }

  /**
   * Queue a query invalidation that will be debounced
   * @param queryKey The query key to invalidate
   */
  invalidate(queryKey: unknown[]) {
    const key = JSON.stringify(queryKey);

    // Store or update the invalidation request
    this.invalidationRequests.set(key, { queryKey, timestamp: Date.now() });

    // Clear existing timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // Set new timeout
    this.timeoutId = setTimeout(() => {
      this.executeInvalidations();
    }, this.debounceMs);
  }

  /**
   * Execute all pending invalidations
   */
  private executeInvalidations() {
    // Convert map values to array and invalidate all at once
    const invalidations = Array.from(this.invalidationRequests.values());

    // Clear the requests
    this.invalidationRequests.clear();
    this.timeoutId = null;

    // Execute all invalidations
    invalidations.forEach(({ queryKey }) => {
      this.queryClient.invalidateQueries({ queryKey });
    });
  }

  /**
   * Force immediate execution of pending invalidations
   */
  flush() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.executeInvalidations();
    }
  }

  /**
   * Clear all pending invalidations without executing them
   */
  clear() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.invalidationRequests.clear();
  }
}

// Create a singleton map of invalidators per query client
const invalidatorMap = new WeakMap<QueryClient, DebouncedQueryInvalidator>();

/**
 * Get or create a debounced invalidator for a query client
 * @param queryClient The TanStack Query client
 * @param debounceMs Debounce delay in milliseconds (default: 1000ms)
 */
export function getDebouncedInvalidator(
  queryClient: QueryClient,
  debounceMs = 1000
): DebouncedQueryInvalidator {
  let invalidator = invalidatorMap.get(queryClient);

  if (!invalidator) {
    invalidator = new DebouncedQueryInvalidator(queryClient, debounceMs);
    invalidatorMap.set(queryClient, invalidator);
  }

  return invalidator;
}
